import {Component, ElementRef, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation, EventEmitter} from '@angular/core';
import * as _ from 'lodash';
import {NetworkApiService} from '../../../services/api/network/online/network/network-api.service';
import {UserPrivilegeService} from '../../../services/common/user-privilege/user-privilege.service';
import {takeWhile} from 'rxjs/operators';
import {animate, style, transition, trigger} from '@angular/animations';
import {DataItemToVisualsMappersService} from '../../services/map/data-item-to-visuals-mappers/data-item-to-visuals-mappers.service';
import {DiagramContextMenuService} from '../../services/map/diagram-context-menu/diagram-context-menu.service';
import {NetworkMapApiService} from '../../services/map/network-map-api/network-map-api.service';
import {DataCacheApiService} from '../../services/map/data-cache-api/data-cache-api.service';
import {NetworkLayersService} from '../../services/map/network-layers/network-layers.service';
import {ConfirmationDialogComponent} from '../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {MapService} from '../../services/map/map/map.service';
import {ContextMenuComponent} from '@perfectmemory/ngx-contextmenu';

@Component({
  selector: 'map-diagram',
  templateUrl: './map-diagram.component.html',
  styleUrls: ['./map-diagram.component.less'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger(
      'inOutAnimation',
      [
        transition(
          ':enter',
          [
            style({opacity: 0}),
            animate('0.3s ease-out',
              style({opacity: 1}))
          ]
        ),
        transition(
          ':leave',
          [
            style({opacity: 1}),
            animate('0.3s ease-in',
              style({opacity: 0}))
          ]
        )
      ]
    )
  ]
})
export class MapDiagramComponent implements OnInit, OnDestroy {

  constructor(private dataItemToVisualsMappersService: DataItemToVisualsMappersService,
              private diagramContextMenuService: DiagramContextMenuService,
              private userPrivilege: UserPrivilegeService,
              private networkLayersService: NetworkLayersService,
              private networkMapApiService: NetworkMapApiService,
              private networkApiService: NetworkApiService,
              private dataCacheApiService: DataCacheApiService,
              private mapService: MapService,
              public dialog: MatDialog) {
  }

  @ViewChild(ContextMenuComponent, {static: false}) public basicMenu: ContextMenuComponent;
  // @ViewChild(InnerLoaderComponent, {static: true}) innerLoaderComponent;

  @Input() diagramApi: any;
  @Output() diagramApiChange = new EventEmitter();

  @Output() diagramItemAdded = new EventEmitter();
  @Output() edgeDragged = new EventEmitter();
  @Output() onDiagramSelect = new EventEmitter();

  initialSession: any;
  rightClickedElement: any;
  isAuthorizedUser: boolean;
  contextMenuData: any;
  peerConnectionSubMenuData: any = null;

  parameters: any = {};
  offsetX: any = 0;
  offsetY: any = 0;

  addItemsMenu: any = {
    addNodeContainerOpen: false,
    addVoipPeerContainerOpen: false,
    isAddItemsMenuOpen: false
  };

  testRouteObservable;

  selectedLayersObject: any = {};

  draggable = {
    // note that data is handled with JSON.stringify/JSON.parse
    // only set simple data or POJO's as methods will be lost
    effectAllowed: 'all',
    disable: false,
    handle: false
  };

  draggedItems: any = {
    nodes: [
      {svg: 'sbc', productType: 'SBC', displayName: 'AC node', type: 'aCDevice'},
      {svg: 'thirdParty', productType: 'THIRD_PARTY', displayName: 'Third party node', type: 'thirdPartyDevice'}
    ],
    voippeers: [
      {svg: 'ip', deviceType: 'IP_PHONES', displayName: 'IP PHONES', type: 'voippeer'},
      {svg: 'sip', deviceType: 'SIP_TRUNK', displayName: 'SIP TRUNK', type: 'voippeer'},
      {svg: 'ip_pbx', deviceType: 'IP_PBX', displayName: 'IP PBX', type: 'voippeer'},
      {svg: 'pbx', deviceType: 'PBX', displayName: 'PBX', type: 'voippeer'},
      {svg: 'pstn', deviceType: 'PSTN', displayName: 'PSTN', type: 'voippeer'},
      {svg: 'teams', deviceType: 'TEAMS', displayName: 'TEAMS', type: 'voippeer'}
    ],
    item: {}
  };

  savedMapConfigurations: any;

  @ViewChild('onlineDiagram', {static: false}) onlineDiagramElem: ElementRef;

  onDrop($event): void {
    this.closeAddItemMenu();
    this.draggedItems.item = $event.data;
    this.offsetX = $event.event.offsetX - 25 + 'px';
    this.offsetY = $event.event.offsetY - 25 + 'px';
    const coordinates = this.diagramApi.domToCanvasTranslate($event.event.offsetX, $event.event.offsetY);

    const isNode = ($event.data.type === 'aCDevice' || $event.data.type === 'thirdPartyDevice');

    this.diagramItemAdded.emit({
      x: coordinates.x,
      y: coordinates.y,
      type: $event.data.type,
      productType: isNode ? $event.data.productType : null,
      deviceType: !isNode ? $event.data.deviceType : null
    });
  }

  ngOnInit(): void {
    this.initialSession = !_.isEqual(sessionStorage.getItem('online-diagram-session'), 'true');
    this.isAuthorizedUser = this.userPrivilege.isAuthorized();
    this.initializeMapConfigurations();
    this.testRouteObservable = this.mapService.onTestRoute().subscribe(() => {
      this.unselectAllElementsOfNetwork();
    });

    this.resetLayersSelection();
  }


  scopeDestroyForBroadcasts = () => {
  }

  ngOnDestroy(): void {
    this.scopeDestroyForBroadcasts();
    this.testRouteObservable.unsubscribe();
  }

  stopPropagation(event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDiagramReady(diagramApiLocal): void {
    // wait until cache data is ready
    let dataReady: boolean = this.dataCacheApiService.getDataReady().value;
    if (dataReady === true) {
      this.initDiagram(diagramApiLocal);
    } else {
      this.dataCacheApiService.getDataReady().pipe(takeWhile(() => dataReady === false)).subscribe(x => {
        if (x === true) {
          dataReady = true;
          this.initDiagram(diagramApiLocal);
        }
      });
    }

  }

  initDiagram(diagramApiLocal): void {
    this.diagramApi = diagramApiLocal;
    /*this.sharedEventService.broadcast('onlineDiagramReady', this.diagramApi);*/
    this.diagramApiChange.emit(this.diagramApi);

    if (this.initialSession) {
      this.initializeDiagramWithData(true);
      sessionStorage.setItem('online-diagram-session', 'true');
    } else {
      this.initializeDiagramWithData(false);
    }
    // this.innerLoaderComponent.showContent();
  }

  onSelect($event): void {
    // this.sharedEventService.broadcast('onlineDiagramSelectionChanged', $event.nodes, $event.edges);
    this.onDiagramSelect.emit($event);
  }

  onEdgeDragged($event): void {
    // this.sharedEventService.broadcast('onlineDiagramEdgeDragged', $event.draggedEdgeObject);
    this.edgeDragged.emit($event.draggedEdgeObject);
  }

  onContextMenu($event): void {
    if (_.isNil($event.contextMenuObject.element)) {
      return;
    }

    this.rightClickedElement = $event.contextMenuObject.element;
    this.contextMenuData = this.diagramContextMenuService.prepareContextMenuActions(this.diagramApi,
      this.rightClickedElement);

    this.peerConnectionSubMenuData = this.rightClickedElement.connectionType === 'peerConnection' ?
      this.diagramContextMenuService.prepareLockPeerConnectionSubMenu(this.rightClickedElement) : null;
  }

  getClusteredEdgePropertiesByEdges = (edgesInCluster) => {
    return this.dataItemToVisualsMappersService.getClusteredEdgeProperties(edgesInCluster);
  }

  getClusteredNodePropertiesByNodes = (nodesInCluster) => {
    return this.dataItemToVisualsMappersService.getClusteredNodeProperties(nodesInCluster);
  }

  onDiagramClick(event): void {

  }

  onDragStart(event): void {

  }

  closeAllContainersScope(): void {
    this.closeAllContainers();
  }

  updateMapConfigurations(): void {
    localStorage.setItem('MapConfigurations_online', JSON.stringify(this.savedMapConfigurations));
    this.diagramApi.updateMapConfigurations(this.savedMapConfigurations);

    if (!_.isEqual(this.dataItemToVisualsMappersService.getLabelOptions(), this.savedMapConfigurations.labelOptions)) {
      this.dataItemToVisualsMappersService.setLabelOptions(this.savedMapConfigurations.labelOptions);
      this.updateDiagramData();
      this.updateDiagramClusters();
    }

  }

  bringIntoView(): void {
    this.diagramApi.fit();
  }

  sendItemsLocationToServer(): void {
    const draggedElements = this.prepareDraggedObjects();
    const clusters = this.diagramApi.getData().clusters;

    this.networkMapApiService.updateShapesLocation(draggedElements, clusters)
      .then(() => {
        // this.sharedEventService.broadcast('onlineDiagramElementsLocationsSaved');
        this.dataCacheApiService.initDataNow().then(() => {
          this.initializeDiagramWithData(true);
        });
      });
  }

  isSendItemsLocationToServerEnabled(): boolean {
    return this.diagramApi && (this.diagramApi.doShapesDragged() || this.diagramApi.doesClustersStateChanged());
  }

  unselectAllElementsOfNetwork(): void {
    this.diagramApi.unselectAll();
  }

  prepareDraggedObjects(): any {
    const draggedElements = {nodes: [], voipPeers: []};
    const currentLocations = this.diagramApi.getElementsLocation();
    const diagramElements = this.diagramApi.getData().nodesArray;

    diagramElements.forEach((element) => {
      if (element.shapeType === 'node') {
        if (element.dataItem.x !== currentLocations[element.id].x || element.dataItem.y !== currentLocations[element.id].y) {
          draggedElements.nodes.push({
            id: element.dataItem.id,
            x: currentLocations[element.id].x,
            y: currentLocations[element.id].y
          });
        }
      } else if (element.shapeType === 'voipPeer') {
        if (element.dataItem.x !== currentLocations[element.id].x || element.dataItem.y !== currentLocations[element.id].y) {
          draggedElements.voipPeers.push({
            id: element.dataItem.id,
            x: currentLocations[element.id].x,
            y: currentLocations[element.id].y
          });
        }
      }
    });

    return draggedElements;
  }

  updateDiagramData(): void {
    const data = this.fetchData();
    this.diagramApi.updateData(data.nodes, data.edges);
  }

  fetchData(): any {
    const shapes = this.networkMapApiService.getDataForShapes(this.parameters);
    const connections = this.networkMapApiService.getDataForConnections(this.parameters);
    const clusters = this.networkMapApiService.getClusters();

    return (
      {
        nodes: shapes,
        edges: connections,
        clusters,
      }
    );
  }

  initializeDiagramWithData(overrideLocationsAndClusters): void {
    const data = this.fetchData();
    if (overrideLocationsAndClusters) {
      this.diagramApi.setData(data.nodes, data.edges, data.clusters, true);
    } else {
      this.diagramApi.setData(data.nodes, data.edges, undefined, false);
    }
  }

  manualRefresh(): void {
    if (this.diagramApi && this.diagramApi.doShapesDragged()) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '460px',
        data: {
          message: `Unsaved items location will be reset" ?`
        }
      });

      dialogRef.componentInstance.onApply.subscribe(result => {
        this.initializeDiagramWithData(true);
        dialogRef.close();
      });
      /*this.sharedEventService.broadcast(
        'openConfirmationDialogWindow',
        'Unsaved items location will be reset',
        'Refresh',
        () => {
          this.initializeDiagramWithData(true);
          this.sharedEventService.broadcast('closeConfirmationDialogWindow');
        }
      );*/
    } else {
      this.initializeDiagramWithData(true);
    }
  }

  removeDraggedSvg(): void {
    this.draggedItems.item = {};
  }

  closeAddItemMenu(): void {
    this.closeAllContainers();
    this.addItemsMenu.isAddItemsMenuOpen = false;
  }

  closeAllContainers(): void {
    this.addItemsMenu.addNodeContainerOpen = false;
    this.addItemsMenu.addVoipPeerContainerOpen = false;
  }

  searchRefresh(searchObject): void {
    if (searchObject === '') {
      this.dataCacheApiService.initDataNow().then(data => {
        this.setParametersAndFilter(searchObject);
      });
    } else {
      this.setParametersAndFilter(searchObject);
    }
  }

  initializeMapConfigurations(): void {
    const conf = localStorage.getItem('MapConfigurations_online');
    const savedMapConfigurations = conf ? JSON.parse(conf) : {};

    this.savedMapConfigurations = _.merge(
      {},
      {
        advancedConfigurations: {drawAnimated: false}, visjs: {interaction: {hideEdgesOnDrag: false}},
        labelOptions: {shorten: false, length: 255}
      },
      savedMapConfigurations
    );

    this.dataItemToVisualsMappersService.setLabelOptions(this.savedMapConfigurations.labelOptions);
  }

  updateMapWithLayers(): void {
    this.networkLayersService.setSelectedLayers(this.selectedLayersObject);
    // this.sharedEventService.broadcast('OnlineDiagramPeriodicRefresh');
    this.updateDiagramData();
  }

  resetLayersSelection(): void {
    this.selectedLayersObject = this.networkLayersService.getSelectedLayers();
  }

  private updateDiagramClusters(): void {
    const clusters = this.diagramApi.getData().clusters;
    const updatedClusters = this.dataItemToVisualsMappersService.prepareClustersPropertiesForDiagram(clusters);

    this.diagramApi.updateClusters(updatedClusters);
  }

  private setParametersAndFilter(searchObject): void {
    this.parameters.searchObject = searchObject;
    this.parameters.mapFilter = true;
    this.updateDiagramData();
  }
}
